import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createTenant, updateTenant } from '../../services/apiSuperAdmin';

const initialForm = {
    tenant_name: '',
    tenant_slug: '',
    manager_name: '',
    manager_email: '',
    manager_password: '',
};

export default function TenantModal({ isOpen, onClose, tenant = null }) {
    const qc = useQueryClient();
    const isEdit = !!tenant;

    const [form, setForm] = useState(initialForm);
    const [showPass, setShowPass] = useState(false);
    const [changePass, setChangePass] = useState(false); // To toggle password field in Edit mode
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (tenant) {
            const manager = tenant.users?.[0] || {};
            setForm({
                tenant_name: tenant.name,
                tenant_slug: tenant.slug,
                manager_name: manager.name || '',
                manager_email: manager.email || '',
                manager_password: '',
            });
        } else {
            setForm(initialForm);
        }
        setChangePass(false);
        setErrors({});
    }, [tenant, isOpen]);

    const mut = useMutation({
        mutationFn: isEdit
            ? (d) => {
                // Los módulos (incluido Tutor AI) se administran en su propio
                // panel — ver TenantModulesModal. Acá no se tocan settings para
                // no pisar lo que se haya configurado allá.
                const payload = {
                    id: tenant.id,
                    name: d.tenant_name,
                    manager_name: d.manager_name,
                    manager_email: d.manager_email,
                };
                if (changePass && d.manager_password) {
                    payload.manager_password = d.manager_password;
                }
                return updateTenant(payload);
            }
            : createTenant,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
            onClose();
        },
        onError: (err) => {
            if (err.response?.data?.errors) {
                setErrors(err.response.data.errors);
            }
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
        if (errors[name]) setErrors(e => ({ ...e, [name]: null }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});
        mut.mutate(form);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-7 py-5 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                            <span className="material-symbols-outlined text-violet-600 dark:text-violet-400 text-[20px]">
                                {isEdit ? 'edit' : 'domain_add'}
                            </span>
                        </div>
                        <div>
                            <h2 className="font-bold text-base text-slate-900 dark:text-slate-100">
                                {isEdit ? 'Editar Tenant' : 'Nuevo Tenant'}
                            </h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                {isEdit ? 'Actualiza los datos de la organización y su manager' : 'Crea una organización y su manager inicial'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-7 py-6 space-y-6 no-scrollbar">
                    {/* Tenant Section */}
                    <div className="space-y-4">
                        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-violet-500 dark:text-violet-400 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[16px]">business</span>
                            Datos de la Organización
                        </p>
                        <div className="grid grid-cols-1 gap-4">
                            <Field
                                label="Nombre de la empresa"
                                name="tenant_name"
                                value={form.tenant_name}
                                onChange={handleChange}
                                placeholder="Ej: Empresa XYZ"
                                error={errors.tenant_name}
                                required
                            />
                            {!isEdit && (
                                <Field
                                    label="Slug (auto-generado si está vacío)"
                                    name="tenant_slug"
                                    value={form.tenant_slug}
                                    onChange={handleChange}
                                    placeholder="empresa-xyz"
                                    error={errors.tenant_slug}
                                    hint="Solo letras, números y guiones. Sin espacios."
                                />
                            )}
                            {isEdit && (
                                <div className="flex items-start gap-2.5 p-3.5 bg-violet-50/50 dark:bg-violet-950/20 border border-violet-100 dark:border-violet-900/50 rounded-xl mt-2">
                                    <span className="material-symbols-outlined text-[18px] text-violet-500 shrink-0 mt-px">widgets</span>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                                            Módulos y funciones
                                        </span>
                                        <span className="text-[10px] text-slate-500 dark:text-slate-400">
                                            Tutor AI y el resto de los módulos se habilitan desde el botón
                                            “Módulos y funciones” en la fila del tenant.
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Manager Section */}
                    <div className="pt-6 border-t border-dashed border-gray-100 dark:border-slate-800 space-y-4">
                        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-blue-500 dark:text-blue-400 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[16px]">manage_accounts</span>
                            {isEdit ? 'Editar Manager Principal' : 'Manager Inicial'}
                        </p>
                        <div className="grid grid-cols-1 gap-4">
                            <Field
                                label="Nombre del Manager"
                                name="manager_name"
                                value={form.manager_name}
                                onChange={handleChange}
                                placeholder="Ej: Ana García"
                                error={errors.manager_name}
                                required
                            />
                            <Field
                                label="Email del Manager"
                                name="manager_email"
                                type="email"
                                value={form.manager_email}
                                onChange={handleChange}
                                placeholder="manager@empresa.com"
                                error={errors.manager_email}
                                required
                            />

                            {/* Password Section */}
                            <div className="space-y-3">
                                {isEdit ? (
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setChangePass(!changePass)}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                                changePass 
                                                    ? 'bg-amber-50 text-amber-600 border border-amber-200' 
                                                    : 'bg-slate-50 text-slate-600 border border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
                                            }`}
                                        >
                                            <span className="material-symbols-outlined text-[16px]">
                                                {changePass ? 'lock_reset' : 'key'}
                                            </span>
                                            {changePass ? 'Cancelar cambio de contraseña' : 'Cambiar contraseña'}
                                        </button>
                                    </div>
                                ) : null}

                                {(!isEdit || changePass) && (
                                    <div className="animate-in slide-in-from-top-2 duration-200">
                                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                                            {isEdit ? 'Nueva Contraseña' : 'Contraseña'} <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPass ? 'text' : 'password'}
                                                name="manager_password"
                                                value={form.manager_password}
                                                onChange={handleChange}
                                                placeholder="Mín. 8 caracteres, mayúscula y número"
                                                className={`w-full pr-10 border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors
                                                    ${errors.manager_password
                                                        ? 'border-red-400 focus:border-red-500'
                                                        : 'border-gray-200 dark:border-slate-700 focus:border-violet-400 dark:focus:border-violet-500'
                                                    }
                                                    bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100`}
                                                required={!isEdit || changePass}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPass(s => !s)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                                                tabIndex={-1}
                                            >
                                                <span className="material-symbols-outlined text-[18px]">
                                                    {showPass ? 'visibility_off' : 'visibility'}
                                                </span>
                                            </button>
                                        </div>
                                        {errors.manager_password && (
                                            <p className="mt-1 text-xs text-red-500">{errors.manager_password[0]}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Global error */}
                    {mut.isError && !Object.keys(errors).length && (
                        <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-600 dark:text-red-400">
                            <span className="material-symbols-outlined text-[18px] shrink-0">error</span>
                            {mut.error?.response?.data?.message ?? 'Ocurrió un error. Intenta de nuevo.'}
                        </div>
                    )}
                </form>

                {/* Actions */}
                <div className="px-7 py-5 bg-slate-50 dark:bg-slate-800/40 border-t border-gray-100 dark:border-slate-800 flex items-center justify-end gap-3 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={mut.isPending}
                        className="flex items-center gap-2 px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white font-bold rounded-xl text-sm shadow-lg shadow-violet-500/25 transition-all"
                    >
                        {mut.isPending ? (
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <span className="material-symbols-outlined text-[18px]">
                                {isEdit ? 'check_circle' : 'add_business'}
                            </span>
                        )}
                        {isEdit ? 'Guardar Cambios' : 'Crear Tenant'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, name, value, onChange, type = 'text', placeholder, error, hint, required }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors
                    ${error
                        ? 'border-red-400 focus:border-red-500'
                        : 'border-gray-200 dark:border-slate-700 focus:border-violet-400 dark:focus:border-violet-500'
                    }
                    bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400`}
            />
            {hint && !error && <p className="mt-1 text-[10px] text-slate-400 italic">{hint}</p>}
            {error && <p className="mt-1 text-xs text-red-500">{error[0]}</p>}
        </div>
    );
}
